/**
 * ThisWeekStrip — operations-focused hero band on Today.
 *
 * Replaces the old "lifetime stats" feel with "what's happening this
 * week." A studio owner glancing at Today should immediately see:
 *
 *   - Today's shoots (projects scheduled for today)
 *   - This week's deadlines (projects due in the next 7 days)
 *   - Idle leads (inquiries that haven't moved in 3+ days)
 *   - Active projects (in production right now)
 *
 * Each tile is a quick anchor into the relevant page with the
 * appropriate filter pre-applied. Renders nothing when the studio
 * has no projects + no inquiries — empty-state contract.
 */

import { useMemo, useState } from 'react';
import { Camera, CalendarClock, MailOpen, Briefcase } from 'lucide-react';
import { useProjects } from '@/services/projects';
import { useInquiries } from '@/services/inquiries';
import { differenceInDays, isSameDay, parseISO, isAfter, isBefore, addDays } from 'date-fns';
import { MetricDetailSheet } from './MetricDetailSheet';

export function ThisWeekStrip() {
  const { data: projects = [] } = useProjects();
  const { data: inquiries = [] } = useInquiries();
  const [openKey, setOpenKey] = useState<string | null>(null);

  const stats = useMemo(() => {
    const now = new Date();
    const weekFromNow = addDays(now, 7);

    let todayShoots = 0;
    let weekDeadlines = 0;
    let activeProjects = 0;

    for (const p of projects as Array<{
      status?: string | null;
      scheduled_date?: string | null;
      due_date?: string | null;
      shoot_date?: string | null;
      delivery_date?: string | null;
    }>) {
      const status = (p.status ?? '').toLowerCase();
      const isLive =
        status &&
        status !== 'archived' &&
        status !== 'completed' &&
        status !== 'cancelled';
      if (isLive) activeProjects += 1;

      // Resilient column reads — schema has used different names over
      // time. Fall back across all of them so we don't undercount.
      const shootRaw = p.scheduled_date ?? p.shoot_date ?? null;
      const dueRaw = p.due_date ?? p.delivery_date ?? null;

      if (shootRaw) {
        try {
          const d = parseISO(shootRaw);
          if (isSameDay(d, now)) todayShoots += 1;
        } catch { /* malformed date — skip */ }
      }
      if (dueRaw) {
        try {
          const d = parseISO(dueRaw);
          if (isAfter(d, now) && isBefore(d, weekFromNow)) weekDeadlines += 1;
        } catch { /* malformed date — skip */ }
      }
    }

    let idleLeads = 0;
    for (const inq of inquiries as Array<{
      status?: string;
      submittedAt?: string;
      submitted_at?: string;
    }>) {
      const status = (inq.status ?? '').toLowerCase();
      const isOpen = status === 'pending' || status === 'lead' || status === '';
      if (!isOpen) continue;
      const submitted = inq.submittedAt ?? inq.submitted_at;
      if (!submitted) continue;
      try {
        const d = parseISO(submitted);
        if (differenceInDays(now, d) >= 3) idleLeads += 1;
      } catch { /* malformed — skip */ }
    }

    return { todayShoots, weekDeadlines, idleLeads, activeProjects };
  }, [projects, inquiries]);

  const noActivity = projects.length === 0 && inquiries.length === 0;
  if (noActivity) return null;

  const tiles = [
    {
      key: 'today-shoots',
      icon: Camera,
      label: "Today's shoots",
      value: stats.todayShoots,
      tone: 'primary' as const,
      href: '/calendar',
      cta: 'Open calendar',
      context: `Projects with today's date on the schedule: ${stats.todayShoots}.`,
    },
    {
      key: 'week-deadlines',
      icon: CalendarClock,
      label: 'Due this week',
      value: stats.weekDeadlines,
      tone: (stats.weekDeadlines > 0 ? 'warn' : 'muted') as 'warn' | 'muted',
      href: '/projects',
      cta: 'Open projects',
      context: `Projects with a due date in the next 7 days: ${stats.weekDeadlines}.`,
    },
    {
      key: 'idle-leads',
      icon: MailOpen,
      label: 'Idle leads',
      value: stats.idleLeads,
      tone: (stats.idleLeads > 0 ? 'warn' : 'muted') as 'warn' | 'muted',
      href: '/inquiries',
      cta: 'Open inquiries',
      context: `Open inquiries with no activity for 3+ days: ${stats.idleLeads}.`,
      hint: stats.idleLeads > 0 ? '3+ days quiet' : undefined,
    },
    {
      key: 'active-projects',
      icon: Briefcase,
      label: 'Active projects',
      value: stats.activeProjects,
      tone: 'muted' as const,
      href: '/projects',
      cta: 'Open projects',
      context: `Projects currently live (not archived/completed/cancelled): ${stats.activeProjects}.`,
    },
  ];

  return (
    <section className="space-y-3">
      <div className="flex items-baseline justify-between">
        <div>
          <p className="section-eyebrow">Operations</p>
          <h2 className="section-heading text-xl md:text-2xl text-foreground">
            This week
          </h2>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {tiles.map((t) => (
          <Tile
            key={t.key}
            onClick={() => setOpenKey(t.key)}
            icon={t.icon}
            label={t.label}
            value={t.value}
            tone={t.tone}
            hint={t.hint}
          />
        ))}
      </div>
      {tiles.map((t) => (
        <MetricDetailSheet
          key={t.key}
          open={openKey === t.key}
          onOpenChange={(o) => setOpenKey(o ? t.key : null)}
          icon={t.icon}
          eyebrow="This week"
          label={t.label}
          value={t.value}
          context={t.context}
          cta={{ label: t.cta, href: t.href }}
        />
      ))}
    </section>
  );
}

type Tone = 'primary' | 'warn' | 'muted';

function Tile({
  onClick,
  icon: Icon,
  label,
  value,
  tone,
  hint,
}: {
  onClick: () => void;
  icon: typeof Camera;
  label: string;
  value: number;
  tone: Tone;
  hint?: string;
}) {
  // Tone classes are scoped so warn cells visibly draw the eye while
  // empty/muted ones recede. The whole tile is one tap target — easier
  // on phones than icon-only links.
  // POL-10 — Primary tile used `bg-orbit-orange/5` as its whole
  // background, which added a scroll-visible orange wash to Today.
  // Only the icon tile keeps the orange accent now; the outer card
  // uses a neutral translucent surface so it sits inside the sky
  // palette instead of competing with it.
  // POL-11b — bumped the primary tile from `bg-white/[0.02]` to
  // `bg-white/[0.06]` + added `backdrop-blur-sm` across all tiles so
  // the numbers read clean against the starfield instead of the sky
  // bleeding through and killing legibility.
  // POL-16 — every tile rides `.orbit-box-dark` (ink gradient +
  // shadow) so the boxes hold their edge against the full-page sky.
  const toneRing =
    tone === 'primary'
      ? 'border-orbit-orange/25 bg-white/[0.06] dark:backdrop-blur-md orbit-box-dark'
      : tone === 'warn'
        ? 'border-amber-500/40 bg-amber-500/10 dark:backdrop-blur-md orbit-box-dark'
        : 'border-border/60 bg-card dark:bg-white/[0.06] dark:backdrop-blur-md orbit-box-dark';
  const toneIcon =
    tone === 'primary'
      ? 'bg-orbit-orange/15 text-orbit-orange'
      : tone === 'warn'
        ? 'bg-amber-500/15 text-amber-600'
        : 'bg-muted/60 text-muted-foreground';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group text-left rounded-2xl border p-4 transition-colors hover:border-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary/40 ${toneRing}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className={`h-9 w-9 rounded-xl flex items-center justify-center ${toneIcon}`}>
          <Icon className="w-4 h-4" />
        </div>
        <span className="font-display text-2xl font-semibold tabular-nums text-foreground">
          {value}
        </span>
      </div>
      <div className="mt-3">
        <p className="text-sm font-medium text-foreground leading-tight">{label}</p>
        {hint && (
          <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>
        )}
      </div>
    </button>
  );
}

export default ThisWeekStrip;
